import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

function toTimecode(seconds, fps = 30) {
  const tot = Math.max(0, seconds || 0);
  const h = Math.floor(tot / 3600);
  const m = Math.floor((tot % 3600) / 60);
  const s = Math.floor(tot % 60);
  const f = Math.floor((tot % 1) * fps);
  return [h, m, s, f].map(v => String(v).padStart(2, '0')).join(':');
}

function fromTimecode(tc) {
  if (!tc) return 0;
  const parts = tc.split(':').map(Number);
  if (parts.length === 4) return parts[0] * 3600 + parts[1] * 60 + parts[2] + parts[3] / 30;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
}

const truncate = (str, n = 20) => str && str.length > n ? str.slice(0, n) + '...' : str;

const PlayIcon = () => <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M7 4.5C7 3.4 8.2 2.7 9.1 3.3l11.5 7.5c0.8 0.5 0.8 1.7 0 2.2L9.1 20.7C8.2 21.3 7 20.6 7 19.5V4.5z"/></svg>;
const PauseIcon = () => <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>;
const PrevIcon = () => <svg width="31" height="31" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;
const NextIcon = () => <svg width="31" height="31" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>;
const ClockIcon = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const VolIcon = ({ muted }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    {muted
      ? <><line x1="23" y1="9" x2="17" y2="15" stroke="#e74c3c"/><line x1="17" y1="9" x2="23" y2="15" stroke="#e74c3c"/></>
      : <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>}
  </svg>
);
const ChevronDown = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>;

function Panel({ label, version, versions, onVersionChange, feedbacks, onSeek, seekTo, onPlayToggle, playCmd, isLatest, videoName }) {
  const videoRef = useRef(null);
  const listRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [dropOpen, setDropOpen] = useState(false);
  const [volOpen, setVolOpen] = useState(false);
  const rafRef = useRef(null);
  const lastScrolledRef = useRef(null);

  useEffect(() => {
    if (seekTo && videoRef.current) {
      videoRef.current.currentTime = seekTo.t;
    }
  }, [seekTo]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playCmd) return;
    if (playCmd.action === 'play') v.play(); else v.pause();
  }, [playCmd]);

  const checkFeedback = (t) => {
    const current = feedbacks.find(f => Math.abs(fromTimecode(f.timecode) - t) < 0.5);
    if (current) {
      setSelectedFeedback(current.id);
      if (lastScrolledRef.current !== current.id) {
        lastScrolledRef.current = current.id;
        const el = listRef.current?.querySelector(`[data-id="${current.id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tick = () => {
      const t = v.currentTime;
      setCurrentTime(t);
      checkFeedback(t);
      rafRef.current = requestAnimationFrame(tick);
    };
    const onPlay = () => { rafRef.current = requestAnimationFrame(tick); };
    const onPause = () => cancelAnimationFrame(rafRef.current);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      cancelAnimationFrame(rafRef.current);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, [feedbacks]);

  const handleTimeUpdate = () => {
    const t = videoRef.current?.currentTime || 0;
    setCurrentTime(t);
    checkFeedback(t);
  };

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (playing) { v.pause(); onPlayToggle?.('pause'); }
    else { v.play(); onPlayToggle?.('play'); }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const t = ratio * duration;
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
    onSeek?.(t);
  };

  const handleProgressMouseDown = (e) => {
    const wrap = e.currentTarget;
    const seek = (clientX) => {
      const rect = wrap.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const t = ratio * duration;
      if (videoRef.current) videoRef.current.currentTime = t;
      setCurrentTime(t);
    };
    seek(e.clientX);
    const onMove = (ev) => seek(ev.clientX);
    const onUp = (ev) => {
      seek(ev.clientX);
      onSeek?.(videoRef.current?.currentTime ?? currentTime);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const jumpTo = (f) => {
    setSelectedFeedback(f.id);
    const t = fromTimecode(f.timecode);
    if (videoRef.current) {
      videoRef.current.pause();
      setPlaying(false);
      videoRef.current.currentTime = t;
    }
    onSeek?.(t);
    onPlayToggle?.('pause');
  };

  const prevFeedback = () => {
    if (!feedbacks.length) return;
    const sorted = [...feedbacks].sort((a, b) => fromTimecode(a.timecode) - fromTimecode(b.timecode));
    const t = videoRef.current?.currentTime ?? currentTime;
    let prev = null;
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (fromTimecode(sorted[i].timecode) < t - 0.05) { prev = sorted[i]; break; }
    }
    jumpTo(prev || sorted[sorted.length - 1]);
  };
  const nextFeedback = () => {
    if (!feedbacks.length) return;
    const sorted = [...feedbacks].sort((a, b) => fromTimecode(a.timecode) - fromTimecode(b.timecode));
    const t = videoRef.current?.currentTime ?? currentTime;
    const next = sorted.find(f => fromTimecode(f.timecode) > t + 0.05);
    jumpTo(next || sorted[0]);
  };

  const progressRatio = duration ? currentTime / duration : 0;
  const dotColor = isLatest ? '#5755df' : 'rgba(255,255,255,0.25)';
  const numColor = isLatest ? '#5755df' : 'rgba(255,255,255,0.5)';

  return (
    <div style={st.panel} onClick={() => { setDropOpen(false); setVolOpen(false); }}>
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .ver-row:hover { background: rgba(255,255,255,0.08) !important; }
        input[type=range].vol-slider::-webkit-slider-runnable-track { border-radius: 4px; }
        input[type=range].vol-slider::-moz-range-track { border-radius: 4px; }
        input[type=range].vol-slider::-webkit-slider-thumb { border-radius: 50%; }
        input[type=range].vol-slider::-moz-range-thumb { border-radius: 50%; }
        .prog-thumb { transition: transform .15s; }
        .prog-wrap:hover .prog-track { height: 7px !important; }
        .prog-wrap:hover .prog-thumb { transform: translate(-50%,-50%) scale(1.4) !important; }
      `}</style>
      <div style={st.panelTop}>
        {/* 버전 라벨 */}
        <div style={st.verLabel}>
          <div style={st.verTag} onClick={e => { e.stopPropagation(); setDropOpen(o => !o); }}>
            <span style={{ ...st.verDot, background: dotColor }} />
            <span style={{ ...st.verNum, color: numColor }}>v{version?.version_number}</span>
            <span style={st.verStatus}>{isLatest ? '최신' : '이전'}</span>
            <span style={st.verChevron}><ChevronDown /></span>
          </div>
          <span style={st.verFilename}>{version && truncate(version.original_name ? `v${version.version_number}_${version.original_name.replace(/\.[^.]+$/, '')}` : `v${version.version_number}_${videoName.replace(/\.[^.]+$/, '')}`)}</span>

          {dropOpen && (
            <div style={st.popup} onClick={e => e.stopPropagation()}>
              {versions.map((v, i) => (
                <div key={v.id}>
                  {i > 0 && <div style={st.divider} />}
                  <div className="ver-row" style={st.verRow} onClick={() => { onVersionChange(v); setDropOpen(false); }}>
                    <div style={st.verThumb}>{v.thumbnail_path ? <img src={v.thumbnail_path} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 5 }} /> : <PlayIcon />}</div>
                    <div style={{ flex: 1 }}>
                      <div style={st.verNumSm}>v{v.version_number}{i === 0 && <span style={st.verLatestTag}> 최신</span>}</div>
                      <div style={st.verFileSm}>{truncate(v.original_name ? `v${v.version_number}_${v.original_name.replace(/\.[^.]+$/, '')}` : `v${v.version_number}_${videoName.replace(/\.[^.]+$/, '')}`)}</div>
                      <div style={st.verDateSm}>{v.uploaded_at?.slice(0, 10).replace(/-/g, '/')}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 비디오 박스 */}
        <div style={st.videoBox}>
          {version?.file_path ? (
            <video
              ref={videoRef}
              src={version.file_path}
              style={st.video}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />
          ) : (
            <div style={st.videoArea}><PlayIcon /></div>
          )}
          <div style={st.playerControls} onClick={e => e.stopPropagation()}>
            <div style={st.ctrlRow}>
              <div style={st.ctrlLeft} />
              <div style={st.ctrlCenter}>
                <button style={st.ctrlBtn} onClick={prevFeedback}><PrevIcon /></button>
                <button style={st.ctrlBtn} onClick={nextFeedback}><NextIcon /></button>
                <button style={st.ctrlBtn} onClick={togglePlay}>{playing ? <PauseIcon /> : <PlayIcon />}</button>
                <span style={st.timecode}><span style={st.timecodeCur}>{toTimecode(currentTime)}</span> / {toTimecode(duration)}</span>
              </div>
              <div style={st.ctrlRight}>
                <div style={{ position: 'relative' }}>
                  <button style={st.ctrlBtn} onClick={() => setVolOpen(o => !o)}><VolIcon muted={volume === 0} /></button>
                  {volOpen && (
                    <div style={st.volPopup}>
                      <span style={st.volPct}>{Math.round(volume * 100)}%</span>
                      <input type="range" min={0} max={1} step={0.01} value={volume} style={st.volSlider} className="vol-slider"
                        onChange={e => { const v = Number(e.target.value); setVolume(v); if (videoRef.current) videoRef.current.volume = v; }} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="prog-wrap" style={st.progressWrap} onClick={handleSeek} onMouseDown={handleProgressMouseDown}>
              <div className="prog-track" style={st.progressTrack}>
                <div style={{ ...st.progressFill, width: `${progressRatio * 100}%` }} />
              </div>
              <div className="prog-thumb" style={{ ...st.progressDot, left: `${progressRatio * 100}%` }} />
            </div>
            <div style={st.markerRow}>
              {duration > 0 && feedbacks.map(f => (
                <div key={f.id} style={{ ...st.marker, left: `${(fromTimecode(f.timecode) / duration) * 100}%` }} onClick={() => jumpTo(f)} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 피드백 스크롤 영역 */}
      <div className="no-scrollbar" style={st.panelScroll}>
        <div style={st.fbPanel}>
          <div style={st.fbPanelHeader}>v{version?.version_number} 피드백 {feedbacks.length}개</div>
          <div ref={listRef}>
            {feedbacks.map((f, idx) => (
              <div key={f.id} data-id={f.id}
                style={{ ...st.fbItem, ...(idx === feedbacks.length - 1 ? { borderBottom: 'none', paddingBottom: 0 } : {}), background: selectedFeedback === f.id ? 'rgba(87,85,223,0.08)' : 'transparent' }}
                onClick={() => jumpTo(f)}>
                <div style={st.fbHead}>
                  <div style={st.fbNumBadge}>{idx + 1}</div>
                  <div style={st.fbAuthorWrap}>
                    <div style={st.fbRow1}><span style={st.fbAuthor}>{f.author_name}</span><span style={st.fbDate}>{f.created_at?.slice(0, 16).replace(/-/g, '/')}</span></div>
                    <div style={st.fbTc}><ClockIcon />{f.timecode}</div>
                  </div>
                </div>
                <div style={st.fbTxt}>{f.content}</div>
              </div>
            ))}
            {feedbacks.length === 0 && <div style={st.noFb}>피드백 없음</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VersionCompare() {
  const navigate = useNavigate();
  const { videoId } = useParams();
  const location = useLocation();
  const [videoName, setVideoName] = useState(location.state?.videoName || '');
  const [versions, setVersions] = useState([]);
  const [leftVersion, setLeftVersion] = useState(null);
  const [rightVersion, setRightVersion] = useState(null);
  const [leftFeedbacks, setLeftFeedbacks] = useState([]);
  const [rightFeedbacks, setRightFeedbacks] = useState([]);
  const [synced, setSynced] = useState(false);
  const [seekLeft, setSeekLeft] = useState(null);
  const [seekRight, setSeekRight] = useState(null);
  const [playCmdLeft, setPlayCmdLeft] = useState(null);
  const [playCmdRight, setPlayCmdRight] = useState(null);
  const [globalPlaying, setGlobalPlaying] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === 'Space' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        const action = globalPlaying ? 'pause' : 'play';
        setGlobalPlaying(p => !p);
        setPlayCmdLeft({ action, ts: Date.now() });
        setPlayCmdRight({ action, ts: Date.now() });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [globalPlaying]);

  useEffect(() => {
    axios.get(`/api/videoviewer/versions/index.php?video_id=${videoId}`, { withCredentials: true })
      .then(res => {
        setVersions(res.data);
        if (res.data.length > 0) { setLeftVersion(res.data[0]); fetchFeedbacks(res.data[0].id, 'left'); }
        if (res.data.length > 1) { setRightVersion(res.data[1]); fetchFeedbacks(res.data[1].id, 'right'); }
      })
      .catch(() => navigate('/login'));
    if (!location.state?.videoName) {
      axios.get(`/api/videoviewer/status/index.php?video_id=${videoId}`, { withCredentials: true })
        .then(res => setVideoName(res.data.name || res.data.video_name || ''))
        .catch(() => {});
    }
  }, [videoId]);

  const fetchFeedbacks = async (versionId, side) => {
    const res = await axios.get(`/api/feedbacks/index.php?video_version_id=${versionId}`, { withCredentials: true });
    if (side === 'left') setLeftFeedbacks(res.data);
    else setRightFeedbacks(res.data);
  };

  const handleLeftVersionChange = (v) => { setLeftVersion(v); fetchFeedbacks(v.id, 'left'); };
  const handleRightVersionChange = (v) => { setRightVersion(v); fetchFeedbacks(v.id, 'right'); };
  const handleLeftSeek = (t) => { if (synced) setSeekRight({ t, ts: Date.now() }); };
  const handleRightSeek = (t) => { if (synced) setSeekLeft({ t, ts: Date.now() }); };
  const handleLeftPlayToggle = (action) => { if (synced) setPlayCmdRight({ action, ts: Date.now() }); };
  const handleRightPlayToggle = (action) => { if (synced) setPlayCmdLeft({ action, ts: Date.now() }); };

  return (
    <div style={s.layout}>
      <div style={s.topbar}>
        <div style={s.topbarLeft}>
          <button style={s.backBtn} onClick={() => navigate(-1)}>
            <svg width="9" height="16" viewBox="0 0 7 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 1 1 6 6 11"/></svg>
            이전 화면으로 돌아가기
          </button>
        </div>
        <div style={s.topbarRight}>
          <button style={s.compareActiveBtn}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 6H3"/><polyline points="7 2 3 6 7 10"/><path d="M3 18h18"/><polyline points="17 14 21 18 17 22"/></svg>
            버전 비교 중
          </button>
          <button
            style={{ ...s.syncBtn, color: synced ? '#f97316' : 'rgba(255,255,255,0.6)', borderColor: synced ? '#f97316' : 'rgba(255,255,255,0.2)' }}
            onClick={() => setSynced(sc => !sc)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
            {synced ? '재생 동기화 중' : '재생 동기화'}
          </button>
        </div>
      </div>

      <div style={s.compareBody}>
        <Panel
          label="left" version={leftVersion} versions={versions}
          onVersionChange={handleLeftVersionChange}
          feedbacks={leftFeedbacks}
          onSeek={handleLeftSeek}
          seekTo={seekLeft}
          onPlayToggle={handleLeftPlayToggle}
          playCmd={playCmdLeft}
          isLatest={versions[0]?.id === leftVersion?.id}
          videoName={videoName}
        />
        <Panel
          label="right" version={rightVersion} versions={versions}
          onVersionChange={handleRightVersionChange}
          feedbacks={rightFeedbacks}
          onSeek={handleRightSeek}
          seekTo={seekRight}
          onPlayToggle={handleRightPlayToggle}
          playCmd={playCmdRight}
          isLatest={versions[0]?.id === rightVersion?.id}
          videoName={videoName}
        />
      </div>
    </div>
  );
}

const s = {
  layout: { height: '100vh', background: '#191919', fontFamily: "'Pretendard', sans-serif", display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  topbar: { height: 56, background: '#191919', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px', flexShrink: 0 },
  topbarLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#fff', fontSize: '13pt', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 10 },
  topbarRight: { display: 'flex', alignItems: 'center', gap: 24 },
  compareActiveBtn: { display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: '#fff', fontSize: '13pt', cursor: 'default', fontFamily: 'inherit', fontWeight: 500 },
  syncBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'transparent', border: '0.56px solid', borderRadius: 8, padding: '5px 14px', minWidth: 160, fontSize: '13pt', cursor: 'pointer', fontFamily: 'inherit' },
  compareBody: { flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, padding: '14px 20px 20px', minHeight: 0 },
};

const st = {
  panel: { display: 'flex', flexDirection: 'column', minHeight: 0, gap: 10 },
  panelTop: { flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  panelScroll: { flex: 1, overflowY: 'auto', minHeight: 0, scrollbarWidth: 'none', msOverflowStyle: 'none' },
  verLabel: { display: 'flex', alignItems: 'center', gap: 8, position: 'relative' },
  verTag: { display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none' },
  verDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0, display: 'inline-block' },
  verNum: { fontSize: '12pt', fontWeight: 700 },
  verStatus: { fontSize: '10pt', color: 'rgba(255,255,255,0.4)' },
  verFilename: { fontSize: '9pt', color: 'rgba(255,255,255,0.35)' },
  verChevron: { color: 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center' },
  popup: { position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: '#2a2a2a', border: '0.56px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 6, zIndex: 300, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', minWidth: 320 },
  verRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 7, cursor: 'pointer' },
  verThumb: { width: 44, height: 30, background: '#383838', borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'rgba(255,255,255,0.25)', fontSize: 9 },
  verNumSm: { fontSize: '11pt', fontWeight: 600, color: '#fff' },
  verLatestTag: { fontSize: 12, color: '#5755df', marginLeft: 4 },
  verFileSm: { fontSize: '10pt', color: '#fff', marginTop: 1 },
  verDateSm: { fontSize: '9pt', color: 'rgba(255,255,255,0.4)' },
  divider: { height: 1, background: 'rgba(255,255,255,0.07)', margin: '3px 0' },
  videoBox: { background: '#000', borderRadius: 6, overflow: 'hidden' },
  videoArea: { background: '#000', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.25)' },
  video: { width: '100%', aspectRatio: '16/9', display: 'block', objectFit: 'contain', background: '#000' },
  playerControls: { background: '#111', padding: '8px 12px 10px', display: 'flex', flexDirection: 'column', gap: 5 },
  ctrlRow: { display: 'flex', alignItems: 'center' },
  ctrlLeft: { flex: 1 },
  ctrlCenter: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 },
  ctrlRight: { flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6 },
  ctrlBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 2, borderRadius: 4, outline: 'none' },
  timecode: { fontSize: '13pt', color: 'rgba(255,255,255,0.4)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  timecodeCur: { color: '#fff' },
  volPopup: { position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)', background: '#2a2a2a', border: '0.56px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '10px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 36, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', zIndex: 100 },
  volPct: { fontSize: 12, color: 'rgba(255,255,255,0.4)' },
  volSlider: { writingMode: 'vertical-lr', direction: 'rtl', width: 4, height: 80, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', outline: 'none', borderRadius: 2 },
  progressWrap: { width: '100%', height: 20, margin: '-8px 0', display: 'flex', alignItems: 'center', position: 'relative', cursor: 'pointer' },
  progressTrack: { width: '100%', height: 4, background: 'rgba(255,255,255,0.15)', borderRadius: 2, overflow: 'hidden', transition: 'height .15s' },
  progressFill: { height: '100%', background: '#fff', borderRadius: 2 },
  progressDot: { width: 9, height: 9, background: '#fff', borderRadius: '50%', position: 'absolute', top: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' },
  markerRow: { position: 'relative', height: 9, width: '100%' },
  marker: { position: 'absolute', width: 9, height: 9, background: '#ffbc00', borderRadius: '50%', top: 0, transform: 'translateX(-50%)', cursor: 'pointer', border: '1.5px solid #111' },
  fbPanel: { background: '#28292a', borderRadius: 10, padding: 14 },
  fbPanelHeader: { fontSize: '9pt', color: 'rgba(255,255,255,0.3)', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.07)' },
  fbItem: { padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer', borderRadius: 6 },
  fbHead: { display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 },
  fbNumBadge: { width: 20, height: 20, borderRadius: '50%', background: '#ffbc00', color: '#fff', fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 },
  fbAuthorWrap: { flex: 1 },
  fbRow1: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
  fbAuthor: { fontSize: '11pt', fontWeight: 500, color: '#f7f7f7' },
  fbDate: { fontSize: '9pt', color: 'rgba(255,255,255,0.35)' },
  fbTc: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '9pt', color: '#73add8', cursor: 'pointer' },
  fbTxt: { fontSize: '11pt', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 },
  noFb: { color: 'rgba(255,255,255,0.2)', fontSize: '11pt', textAlign: 'center', padding: '20px 0' },
};
